import "./SaturationAndDecay.scss";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import WidgetContainer from "../../Components/WidgetContainer";
import ChartContainer from "../../Components/ChartContainer";
import { DownloadDropdown, InfoTooltip, ToggleSwitch } from "../../Components";
import useLocation from "../../utils/hooks/useLocation";
import LineChart from "../../Components/Charts/LineChart";
import { decayDataFormatter, saturationDataFormatter } from "./DataFormatters";
import { exportToExcel, downloadPNG } from "../../utils/download-utils";
import {
  numberFormatter,
  capitalizeWords,
  currencyFormatter,
  CONVERSION_FORMATTER_TYPE,
} from "../MMMUtils";
import { CartesianGrid, Line, LineChart as LC, Tooltip, YAxis, XAxis } from "recharts";
import * as R from "ramda";
import AutoSizer from "react-virtualized-auto-sizer";
import {
  CARTESIAN_GRID_STROKE,
  CARTESIAN_GRID_STROKE_WIDTH,
  TICK_STYLE,
} from "../../Components/Charts/ChartConstants";
import { colorMap as colors, Neutral500 } from "../../utils/colors";
import { useDynamicYAxisWidth } from "../../Components/Charts/ChartUtils";
import { MdOutlineWarningAmber } from "react-icons/md";
import { TEST_COMPANIES } from "@blisspointmedia/bpm-types/dist/TestCompanies";
import { SatData, DecayData } from "@blisspointmedia/bpm-types/dist/MMM";

const CHART_CONTAINER_HEIGHT = 516;
const MEDIAN_DOT_SIZE = 7;
const INACTIVE_COLOR = "#d3d3d3";

interface SatAndDecayProps {
  disabledPlatform: boolean | undefined;
  kpiType: string;
  satData: SatData[];
  decayData: DecayData[];
  renderOmitDarkInputsToggle: boolean;
  omitDarkInputs: boolean;
  setOmitDarkInput: React.Dispatch<React.SetStateAction<boolean>>;
}

const SatAndDecay: React.FC<SatAndDecayProps> = ({
  disabledPlatform,
  kpiType,
  satData,
  decayData,
  renderOmitDarkInputsToggle,
  omitDarkInputs,
  setOmitDarkInput,
}) => {
  let { company } = useLocation();
  if (TEST_COMPANIES.includes(company)) {
    company = "test";
  }
  const [decayDataFormatted, setDecayDataFormatted] = useState(
    {} as { newD: any[]; lines: any[]; groupedLines: any[] }
  );
  const [formattedSatData, setFormattedSatData] = useState({} as any);
  const [activeLine, setActiveLine] = useState(null);
  const [hoveredLegend, setHoveredLegend] = useState(null);
  const [activeGroup, setActiveGroup] = useState(null);
  const companyEqualsTest = TEST_COMPANIES.includes(company);

  const { xAxisHeight, yAxisWidth, setChartRef } = useDynamicYAxisWidth({
    xAxisHeightModifier: x => x + 5,
    yAxisWidthModifier: x => x + 8,
  });

  useEffect(() => {
    if (decayData.length && disabledPlatform !== undefined) {
      setDecayDataFormatted(
        (decayDataFormatter(
          decayData,
          "spend_carryover",
          disabledPlatform,
          companyEqualsTest
        ) as unknown) as {
          newD: any[];
          lines: any[];
          groupedLines: any[];
        }
      );
    }
  }, [company, companyEqualsTest, disabledPlatform, decayData]);

  const excelDownloadSat = useCallback(() => {
    exportToExcel(satData, `${company}_saturation`);
  }, [satData, company]);

  const pngDownloadSat = useCallback(async () => {
    await downloadPNG(".section4 .left .chartContainer", `${company}_saturation`);
  }, [company]);

  const excelDownloadDecay = useCallback(() => {
    exportToExcel(decayData, `${company}_decay`);
  }, [decayData, company]);

  const pngDownloadDecay = useCallback(async () => {
    await downloadPNG(".section4 .right .chartContainer", `${company}_decay`);
  }, [company]);

  const CustomizedDot = props => {
    const { cx, cy, stroke, payload, saturationData } = props;

    if (
      saturationData.medians[payload.name].dataPoint !== payload.adstock_spend ||
      saturationData.medians[payload.name].median === null
    ) {
      return null;
    }

    return (
      <svg>
        <polygon
          points={`${cx},${cy - MEDIAN_DOT_SIZE} ${cx + MEDIAN_DOT_SIZE},${cy} ${cx},${
            cy + MEDIAN_DOT_SIZE
          } ${cx - MEDIAN_DOT_SIZE},${cy}`}
          fill={stroke}
        />
      </svg>
    );
  };

  useMemo(() => {
    if (!R.isEmpty(satData) && disabledPlatform !== undefined) {
      setFormattedSatData(saturationDataFormatter(satData, disabledPlatform, companyEqualsTest));
    }
  }, [companyEqualsTest, disabledPlatform, satData]);

  const colorMap = useMemo(() => {
    if (!R.isEmpty(formattedSatData)) {
      return colors(formattedSatData.newD);
    }
  }, [formattedSatData]);

  const handleLegendClick = useCallback(
    (line, group?) => {
      if (activeLine === line.name) {
        setActiveLine(null);
        setActiveGroup(null);
      } else {
        setActiveLine(line.name);
        setActiveGroup(group.channel);
      }
    },
    [activeLine]
  );

  const handleLegendEnter = useCallback(
    line => {
      hoveredLegend === line.name ? setHoveredLegend(null) : setHoveredLegend(line.name);
    },
    [hoveredLegend]
  );

  const tooltip = (
    <Tooltip
      content={({ active, label, payload }) => {
        if (active && payload && payload.length) {
          let items = R.sortBy(
            (item: any) => -Math.abs(item.value),
            payload?.map(item => ({
              label: item.name as string,
              value: item.value as number,
              color: item.stroke,
            })) || []
          );

          return (
            <div className="satChartTooltip">
              <div className="itemList">
                {items.map((item, i) => {
                  const { label, value, color } = item;
                  if (!value && value !== 0) {
                    return <></>;
                  }
                  const cpa = payload[0].payload.adstock_spend / payload[0].payload.response;

                  return (
                    <div className="itemRow" key={i}>
                      <div className="itemWrapper dataLabel">
                        <div></div>
                        <div style={{ display: "flex", alignItems: "center", padding: "0 12px" }}>
                          <div className="line" style={{ backgroundColor: color }} />
                          <div className="name">{`${label}: `}</div>
                        </div>
                      </div>
                      <div className="itemWrapper">
                        <div className="satTooltipHeaderItem">Median Spend:</div>
                        <div className="value">
                          {currencyFormatter.format(
                            formattedSatData.medians[payload[0].payload.name].median,
                            0
                          )}
                        </div>
                      </div>
                      <div className="itemWrapper">
                        <div className="satTooltipHeaderItem">Spend:</div>
                        <div className="value">
                          {currencyFormatter.format(payload[0].payload.adstock_spend, 0)}
                        </div>
                      </div>
                      <div className="itemWrapper">
                        <div className="satTooltipHeaderItem">{`${
                          kpiType === "revenue" ? "Revenue" : "KPI Volume"
                        }:`}</div>
                        <div className="value">
                          {CONVERSION_FORMATTER_TYPE[kpiType].format(
                            payload[0].payload.response,
                            0
                          )}
                        </div>
                      </div>
                      {kpiType === "revenue" && (
                        <div className="itemWrapper">
                          <div className="satTooltipHeaderItem">ROAS:</div>
                          <div className="value">
                            {(
                              payload[0].payload.response / payload[0].payload.adstock_spend
                            ).toFixed(2)}
                          </div>
                        </div>
                      )}
                      {kpiType === "conversion" && (
                        <div className="itemWrapper">
                          <div className="satTooltipHeaderItem">CPA:</div>
                          <div className="value">{`${
                            cpa === Infinity ? 0 : currencyFormatter.format(cpa)
                          }`}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }
        return null;
      }}
      isAnimationActive={false}
    />
  );

  const buildLegendByGroupedArea = useCallback(
    groupedAreas => {
      return groupedAreas.map((group, i) => {
        return (
          <div className="legendItem" key={`satDecayLegenditem${i}`}>
            <div className="legendValue">
              <div>
                <div className="legendHeaderWrapper" style={{ cursor: "default" }}>
                  <div
                    className={`legendHeader ${
                      activeGroup === group.channel || activeGroup === null ? "" : "inactiveLegend"
                    }`}
                  >
                    {group.channel}
                  </div>
                </div>
                {group.areas.map((area, j) => {
                  const isActiveArea = activeLine === null ? true : activeLine === area.name;
                  const isHoveredLegend =
                    hoveredLegend === null ? true : hoveredLegend === area.name;
                  const activeColor = isActiveArea ? colorMap[area.name] : INACTIVE_COLOR;
                  const hoverColor = isHoveredLegend ? colorMap[area.name] : INACTIVE_COLOR;
                  return (
                    <div
                      key={`satDecayLegendsubHeader${j}`}
                      className="subHeaderWrapper"
                      onClick={() => handleLegendClick(area, group)}
                      onMouseEnter={() => handleLegendEnter(area)}
                      onMouseLeave={() => setHoveredLegend(null)}
                    >
                      <div
                        className="line"
                        style={{
                          backgroundColor: `${activeLine ? activeColor : hoverColor}`,
                        }}
                      />
                      <div className="subHeaderTextWrap">
                        <div className={`subHeader1 ${isActiveArea ? "" : "inactiveLegend"}`}>
                          {area.grouping.platform}
                        </div>
                        {area.grouping.platform &&
                          (area.grouping.tactic || area.grouping.brand_nonbrand) && (
                            <div style={{ whiteSpace: "pre-wrap" }}> – </div>
                          )}
                        <div className={`subHeader2 ${isActiveArea ? "" : "inactiveLegend"}`}>{`${
                          area.grouping.tactic ? area.grouping.tactic : ""
                        }${area.grouping.tactic && area.grouping.brand_nonbrand ? ", " : ""}${
                          area.grouping.brand_nonbrand ? area.grouping.brand_nonbrand : ""
                        }`}</div>
                        {area.incrementality && (
                          <MdOutlineWarningAmber
                            style={{
                              color: `${
                                isActiveArea || activeLine === null ? "orange" : INACTIVE_COLOR
                              }`,
                              marginLeft: "5px",
                            }}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      });
    },
    [activeLine, activeGroup, colorMap, handleLegendClick, handleLegendEnter, hoveredLegend]
  );

  return (
    <WidgetContainer
      bottom={
        <div className="mmmWidgetBottomWrapper">
          <MdOutlineWarningAmber style={{ fontSize: 22, marginRight: 14 }} />
          <div>
            Channels marked with an alert icon lack incrementality testing, and results should be
            interpreted cautiously. <br />
            Incrementality testing is strongly recommended, and we encourage you to contact your
            team to review options.
          </div>
        </div>
      }
      collapsible
      header="Model Insights"
      subHeader="The model estimation process yields business insights related to channel saturation and the lagged nature of consumer response."
      rightActions={
        renderOmitDarkInputsToggle ? (
          <ToggleSwitch
            background="light"
            design="secondary"
            size="lg"
            label="Omit Dark Inputs"
            checked={omitDarkInputs}
            onChange={() => {
              setOmitDarkInput(current => !current);
            }}
            infoTooltipText="Dark inputs are defined as paid media features that account for <1% of spend in the previous quarter. Omitting these inputs allows for focus on active investments only."
          />
        ) : undefined
      }
    >
      {
        <div className="section4" style={{ display: "flex" }}>
          <div className="left" style={{ display: "flex", flexDirection: "column", flex: 1 }}>
            <ChartContainer
              enableHoverDesign
              title="Saturation Curves"
              height={CHART_CONTAINER_HEIGHT}
              rightActions={
                <>
                  <InfoTooltip size="reg">
                    Most media tactics will exhibit some form of diminishing returns, due to
                    additional investment reaching less qualified audiences and/or additional user
                    frequency having diminished impact. The model training process estimates the
                    shape of these saturation curves on a channel by channel basis. Examining these
                    curves yields insight into the scalability of each channel and the optimality of
                    incremental investments (see above section).
                  </InfoTooltip>
                  <DownloadDropdown size="sm" onClickOptions={[excelDownloadSat, pngDownloadSat]} />
                </>
              }
            >
              {!R.isEmpty(formattedSatData) && (
                <>
                  <div className="satWrapper">
                    <div className="satChartTitle">{`${
                      kpiType === "revenue" ? "Revenue" : "KPI Volume"
                    }`}</div>
                    <AutoSizer>
                      {({ width, height }) => (
                        <LC width={width} height={height} ref={setChartRef}>
                          <CartesianGrid
                            stroke={CARTESIAN_GRID_STROKE}
                            strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                            vertical={false}
                          />
                          <XAxis
                            axisLine={false}
                            dataKey="adstock_spend"
                            height={xAxisHeight}
                            tick={TICK_STYLE}
                            tickFormatter={val => `${currencyFormatter.format(val, 0)}`}
                            type="number"
                          />
                          <YAxis
                            axisLine={false}
                            dataKey="response"
                            tick={TICK_STYLE}
                            tickFormatter={val => {
                              const decimals = val > 1000000 ? 1 : 0;
                              return `${CONVERSION_FORMATTER_TYPE[kpiType].format(val, decimals)}`;
                            }}
                            tickLine={false}
                            width={yAxisWidth}
                          />
                          {activeLine && tooltip}

                          {formattedSatData.newD.map(line => {
                            const isActiveLine =
                              activeLine === null ? true : activeLine === line.name;
                            const isHoveredLegend =
                              hoveredLegend === null ? true : hoveredLegend === line.name;
                            const activeColor = isActiveLine ? colorMap[line.name] : Neutral500;
                            const hoverColor = isHoveredLegend ? colorMap[line.name] : Neutral500;

                            if (!isActiveLine && activeLine !== null) {
                              return null;
                            }
                            return (
                              <Line
                                data={line.data}
                                dataKey="response"
                                dot={<CustomizedDot saturationData={formattedSatData} />}
                                isAnimationActive={false}
                                key={line.name}
                                name={line.name}
                                stroke={activeLine ? activeColor : hoverColor}
                                strokeWidth={3}
                              />
                            );
                          })}
                        </LC>
                      )}
                    </AutoSizer>
                  </div>
                  <div className="rightOfChart">
                    <div className="legend">
                      {buildLegendByGroupedArea(
                        R.sortBy(R.compose(R.toLower, R.prop("channel")))(
                          formattedSatData.groupedLines
                        )
                      )}
                    </div>
                    <div className="medianLegendLabel">
                      <svg width="15" height="15" viewBox="0 0 15 15">
                        <polygon points="7.5,0 15,7.5 7.5,15 0,7.5" fill="#1F003F" />
                      </svg>
                      <div className="medianText">
                        Median <br /> spend
                      </div>
                    </div>
                    <div className="satChartXAxisTitle">Spend</div>
                  </div>
                </>
              )}
            </ChartContainer>
          </div>
          <div className="right">
            <ChartContainer
              enableHoverDesign
              height={CHART_CONTAINER_HEIGHT}
              rightActions={
                <>
                  <InfoTooltip size="reg">
                    Few consumers, upon being exposed to an advertisement, drop everything and
                    respond immediately; there is almost always some lag between exposure and
                    response. The model training process estimates the extent of this response lag
                    on a channel by channel basis using a technique known as Adstock. Typically
                    (though not always), we see upper-funnel tactics having longer response lags and
                    lower-funnel tactics converting more quickly. Examining these curves can help us
                    understand channel properties and set appropriate expectations for channel
                    investments.
                  </InfoTooltip>
                  <DownloadDropdown
                    size="sm"
                    onClickOptions={[excelDownloadDecay, pngDownloadDecay]}
                  />
                </>
              }
              title="Spend Decay Curves"
            >
              <div className="decayWrapper">
                <div className="decayChartTitle">Spend Carryover</div>
                {Object.keys(decayDataFormatted).length && (
                  <LineChart
                    customTooltipHeader={val => `Week ${val} Spend Carryover`}
                    data={decayDataFormatted?.newD}
                    lineDataKeys={decayDataFormatted?.lines}
                    groupedAreas={R.sortBy(R.compose(R.toLower, R.prop("channel")))(
                      decayDataFormatted?.groupedLines
                    )}
                    tooltipLabelFormatter={val => capitalizeWords(val, disabledPlatform, true)}
                    tooltipFormatter={val => (val ? `${numberFormatter.format(val, 0)}%` : val)}
                    usePercentageYAxis={true}
                    xAxisDataKey="date"
                    xAxisTickFormatter={val => `Week ${val}`}
                  />
                )}
              </div>
            </ChartContainer>
          </div>
        </div>
      }
    </WidgetContainer>
  );
};

export default SatAndDecay;
